import type { SparklineValue } from './parseSparklineValues';

export type SparklinePlotPoint = {
  index: number;
  x: number;
  y: number | null;
  value: SparklineValue;
};

export type SparklineSegment = {
  points: string;
};

export const SPARKLINE_PADDING_X = 8;
const PADDING_Y = 14;
const PLOT_BOTTOM_PADDING = 8;

export const clientXToSvgX = (clientX: number, rect: DOMRect, svgWidth: number): number => {
  const relativeX = clientX - rect.left;
  if (!rect.width) return 0;
  return (relativeX / rect.width) * svgWidth;
};

export const getSparklineIndexAtSvgX = (valuesLength: number, width: number, svgX: number): number => {
  if (valuesLength <= 1) return 0;

  const plotWidth = Math.max(width - SPARKLINE_PADDING_X * 2, 1);
  const ratio = (svgX - SPARKLINE_PADDING_X) / plotWidth;
  const clampedRatio = Math.max(0, Math.min(1, ratio));
  return Math.round(clampedRatio * (valuesLength - 1));
};

export const buildSparklinePlot = (
  values: SparklineValue[],
  width: number,
  height: number,
): { points: SparklinePlotPoint[]; segments: SparklineSegment[] } | null => {
  const plotWidth = Math.max(width - SPARKLINE_PADDING_X * 2, 1);
  const plotHeight = Math.max(height - PADDING_Y - PLOT_BOTTOM_PADDING, 1);
  const numericValues = values.filter((value): value is number => value !== null);

  if (!numericValues.length) return null;

  const min = Math.min(...numericValues);
  const max = Math.max(...numericValues);
  const range = max - min || 1;
  const points: SparklinePlotPoint[] = values.map((value, index) => {
    const x = SPARKLINE_PADDING_X + (values.length > 1 ? (index / (values.length - 1)) * plotWidth : plotWidth / 2);

    if (value === null) {
      return { index, x, y: null, value };
    }

    const y = PADDING_Y + plotHeight - ((value - min) / range) * plotHeight;
    return { index, x, y, value };
  });

  const segments: SparklineSegment[] = [];
  let activePoints: string[] = [];

  points.forEach(point => {
    if (point.y === null) {
      if (activePoints.length) {
        segments.push({ points: activePoints.join(' ') });
        activePoints = [];
      }
      return;
    }

    activePoints.push(`${point.x},${point.y}`);
  });

  if (activePoints.length) {
    segments.push({ points: activePoints.join(' ') });
  }

  return { points, segments };
};

export const countRenderedSparklinePoints = (segments: SparklineSegment[]): number =>
  segments.reduce((total, segment) => total + segment.points.trim().split(/\s+/).filter(Boolean).length, 0);
